export type ShareResourceType = 'file' | 'folder';

export type ShareKind = 'user' | 'group' | 'link' | 'org';

export type SharePermission = 'read' | 'write';

export type LinkScope = 'anyone' | 'org';

type EffectiveAccessOrigin =
  | 'admin'
  | 'owner'
  | 'user'
  | 'group'
  | 'org'
  | 'link';

export enum ShareErrorCode {
  PASSWORD_REQUIRED = 'PASSWORD_REQUIRED',
  WRONG_PASSWORD = 'WRONG_PASSWORD',
  EXPIRED = 'EXPIRED',
  DISABLED = 'DISABLED',
  LIMIT_REACHED = 'LIMIT_REACHED',
  NOT_FOUND = 'NOT_FOUND',
  NOT_FILE = 'NOT_FILE',
  NOT_IN_SHARE = 'NOT_IN_SHARE',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
  UNKNOWN = 'UNKNOWN',
}

export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

export interface Share {
  id: string;
  kind: ShareKind;
  permission: SharePermission;
  resourceType: ShareResourceType;
  resourceId: string;
  userId: string | null;
  user?: UserSummary | null;
  groupId: string | null;
  token: string | null;
  linkScope: LinkScope | null;
  expiresAt: string | null;
  maxDownloads: number | null;
  downloadCount: number;
  isActive: boolean;
  inherited: boolean;
  inheritedFrom: string | null;
  createdBy: string;
  createdByUser?: UserSummary;
  createdAt: string;
  updatedAt: string;
}

interface PublicShare {
  id: string;
  kind: ShareKind;
  linkScope: LinkScope | null;
  resourceType: ShareResourceType;
  resourceId: string;
  token: string;
  expiresAt: string | null;
  maxDownloads: number | null;
  downloadCount: number;
  hasPassword: boolean;
}

// ─── Discriminated CreateShareDto ─────────────────────────────────────────

interface BaseCreateShareDto {
  resourceType: ShareResourceType;
  resourceId: string;
  permission?: SharePermission;
}

export interface CreateUserShareDto extends BaseCreateShareDto {
  kind: 'user';
  userId: string;
}

export interface CreateGroupShareDto extends BaseCreateShareDto {
  kind: 'group';
  groupId: string;
}

export interface CreateLinkShareDto extends BaseCreateShareDto {
  kind: 'link';
  linkScope: LinkScope;
  password?: string;
  expiresAt?: string;
  maxDownloads?: number;
}

export interface CreateOrgShareDto
  extends Pick<BaseCreateShareDto, 'resourceType' | 'resourceId'> {
  kind: 'org';
}

export type CreateShareDto =
  | CreateUserShareDto
  | CreateGroupShareDto
  | CreateLinkShareDto
  | CreateOrgShareDto;

export interface UpdateShareDto {
  permission?: SharePermission;
  expiresAt?: string | null;
  password?: string | null;
  maxDownloads?: number | null;
  isActive?: boolean;
}

export interface EffectiveAccess {
  hasAccess: boolean;
  permission?: SharePermission;
  origin?: EffectiveAccessOrigin;
  isOwner: boolean;
  canManageShares: boolean;
  inherited: boolean;
  inheritedFrom?: {
    resourceType: string;
    resourceId: string;
    name?: string;
  } | null;
}

// ─── Public viewer payloads ───────────────────────────────────────────────

export interface SharedFileResource {
  id: string;
  name: string;
  originalName: string;
  size: number;
  mimeType: string;
  duration?: number | null;
  streamUrl: string;
}

export interface ShareStreamResponse {
  file: {
    id: string;
    name: string;
    originalName: string;
    size: number;
    mimeType: string;
    duration?: number | null;
  };
  streamUrl: string;
  expiresIn: number;
}

export interface SharedFolderChild {
  id: string;
  name: string;
}

export interface SharedFolderResource {
  id: string;
  name: string;
  path: string;
  children: SharedFolderChild[];
  files: SharedFileResource[];
}

export interface ShareAccessResponse {
  share: PublicShare;
  resource: SharedFileResource | SharedFolderResource;
}

export interface ShareDownloadResponse {
  downloadUrl: string;
  fileName: string;
  expiresIn: number;
}
